import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import {
  inquirySubjects,
  inquiryTimings,
  inquiryAssurances,
} from "@/brand/content";
import { ChamberChrome } from "@/components/common/ChamberChrome";
import { FormField } from "@/components/form/FormField";
import { FormSelect } from "@/components/form/FormSelect";
import { FormTextarea } from "@/components/form/FormTextarea";

/**
 * InquiryFormSection — Soft Parchment chamber (VII. INSTITUTIONAL INTAKE).
 *
 * The RARE REVEAL chamber on the site. Authoring moves added:
 *   - Chamber chrome rail at top (Roman numeral VII.)
 *   - Vertical brass running-head "VII / INSTITUTIONAL INTAKE" on the
 *     left viewport margin (xl+ only)
 *   - Section number tag (§ 07)
 */
export function InquiryFormSection() {
  return (
    <section
      id="contact"
      data-testid="inquiry-form-section"
      style={{
        background: C.surfaceLight2,
        color: C.textOnLight,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChamberChrome
        numeral="VII."
        title="Institutional intake"
        category="Contact"
        meta="Eight fields"
        tone="light"
      />

      {/* Vertical running head — viewport left margin */}
      <span
        aria-hidden="true"
        className="hidden xl:flex absolute items-center"
        style={{
          left: 24,
          top: "8rem",
          bottom: "4rem",
          width: 24,
          writingMode: "vertical-rl",
          transform: "rotate(180deg)",
        }}
      >
        <span
          className="font-inter text-[10px] uppercase font-bold tabular-nums"
          style={{ color: C.accentMetal, letterSpacing: "0.42em" }}
        >
          VII &nbsp;·&nbsp; Institutional Intake &nbsp;·&nbsp; Contact
        </span>
      </span>

      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-16">
          {/* ── Intro rail ─────────────────────────────────────────── */}
          <div className="lg:col-span-5 lg:col-start-1">
            <div className="flex items-center gap-3 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-10"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold tabular-nums"
                style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
              >
                § 07 &nbsp;·&nbsp; For boards, EDs &amp; HR leaders
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(2.4rem, 4.4vw, 3.6rem)",
                fontWeight: 400,
                lineHeight: 1.02,
                letterSpacing: "-0.012em",
              }}
            >
              Begin a
              <br />
              confidential
              <br />
              inquiry.
            </h2>
            <p
              className="mt-8 font-inter leading-[1.7] max-w-[42ch]"
              style={{ color: C.inkOnLight, fontSize: "1.02rem" }}
            >
              A short, deliberate inquiry is enough. Katrina reads every
              submission personally. We respond within two business days with
              either a time to talk or a thoughtful referral elsewhere if
              Faith&nbsp;1st isn’t the right fit.
            </p>

            <dl className="mt-14 space-y-0">
              {inquiryAssurances.map(([t, b], i) => (
                <div
                  key={t}
                  className="py-6 flex items-baseline gap-6"
                  style={{
                    borderTop:
                      i === 0 ? `1px solid ${C.borderSoft}` : "none",
                    borderBottom: `1px solid ${C.borderSoft}`,
                  }}
                >
                  <span
                    className="font-inter text-[10px] uppercase font-bold tabular-nums shrink-0"
                    style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <dt
                      className="font-inter text-[10.5px] uppercase font-semibold mb-1.5"
                      style={{
                        color: C.accentFunctional,
                        letterSpacing: "0.26em",
                      }}
                    >
                      {t}
                    </dt>
                    <dd
                      className="font-inter leading-[1.6]"
                      style={{ color: C.inkOnLight, fontSize: "0.95rem" }}
                    >
                      {b}
                    </dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>

          {/* ── Form rail — col 7-12 ─────────────────────────────────── */}
          <div className="lg:col-span-7 lg:col-start-6">
            <form
              data-testid="inquiry-form"
              onSubmit={(e) => e.preventDefault()}
              className="space-y-10"
            >
              <p
                className="font-inter text-[10px] uppercase font-semibold pb-4"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                  borderBottom: `1px solid ${C.borderSoft}`,
                }}
              >
                Institutional intake &nbsp;·&nbsp; 8 fields
              </p>

              {/* row 1 — name + organization */}
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-10">
                <FormField
                  label="Full name"
                  name="name"
                  required
                  index="01"
                  testId="field-name"
                />
                <FormField
                  label="Organization"
                  name="organization"
                  required
                  index="02"
                  testId="field-organization"
                />
              </div>

              {/* row 2 — role + email */}
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-10">
                <FormField
                  label="Role / title"
                  name="role"
                  required
                  index="03"
                  testId="field-role"
                />
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  required
                  index="04"
                  testId="field-email"
                />
              </div>

              {/* row 3 — subject */}
              <FormSelect
                label="What you’re seeking support with"
                name="subject"
                options={inquirySubjects}
                required
                index="05"
                testId="field-subject"
              />

              {/* row 4 — challenge */}
              <FormTextarea
                label="A brief description of the moment"
                name="challenge"
                hint="Two or three sentences is enough. Please don’t include sensitive incident details here — we’ll surface those carefully in the discovery conversation."
                required
                index="06"
                testId="field-challenge"
              />

              {/* row 5 — timing + referral */}
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-10">
                <FormSelect
                  label="Timing"
                  name="timing"
                  options={inquiryTimings}
                  index="07"
                  testId="field-timing"
                />
                <FormField
                  label="How you heard about Faith 1st"
                  name="referral"
                  index="08"
                  testId="field-referral"
                />
              </div>

              <p
                className="font-inter text-[12px] leading-[1.7] pt-2 max-w-[60ch]"
                style={{ color: C.inkOnLightSoft }}
              >
                By submitting, you confirm you’re reaching out on behalf of
                your organization. Every inquiry is treated confidentially.
              </p>

              <div
                className="pt-8 flex flex-col sm:flex-row sm:items-center gap-6"
                style={{ borderTop: `1px solid ${C.borderSoft}` }}
              >
                <button
                  type="submit"
                  data-testid="inquiry-submit"
                  className="group inline-flex items-center justify-between gap-4 px-7 py-4 font-inter text-[12px] uppercase font-semibold transition-colors"
                  style={{
                    background: C.bgDark,
                    color: C.textOnDark,
                    letterSpacing: "0.24em",
                    border: `1px solid ${C.bgDark}`,
                    minWidth: 260,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = C.accentMetal;
                    e.currentTarget.style.borderColor = C.accentMetal;
                    e.currentTarget.style.color = C.bgDark;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = C.bgDark;
                    e.currentTarget.style.borderColor = C.bgDark;
                    e.currentTarget.style.color = C.textOnDark;
                  }}
                >
                  Submit inquiry
                  <ArrowRight
                    size={14}
                    weight="bold"
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
                <span
                  className="font-inter text-[10.5px] uppercase font-semibold"
                  style={{
                    color: C.inkOnLightSoft,
                    letterSpacing: "0.26em",
                  }}
                >
                  Response within 2 business days
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
